"use client"

import { useState } from "react"
import { COLOR_OPTIONS } from "@/schema/colors"
import { Button } from "@ui/shad/button"
import { Label } from "@ui/shad/label"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/shad/popover"
import { cn } from "@ui/utils"
import {
  Banknote,
  Bell,
  Bot,
  Brain,
  Briefcase,
  Calendar,
  ChartLine,
  ChartPie,
  CheckCircle,
  Clock,
  Coins,
  DollarSign,
  FileText,
  Handshake,
  Landmark,
  Mail,
  Phone,
  PiggyBank,
  Send,
  Sparkles,
  Star,
  Users,
  WandSparkles,
  Zap,
  type LucideIcon,
} from "lucide-react"

import { ColorPicker } from "@/components/_ui/color-picker"

export const ICON_DEFAULT = "wand"
export const COLOR_DEFAULT = COLOR_OPTIONS.blue

const ICON_OPTIONS: { name: string; icon: LucideIcon }[] = [
  // AI & Magic
  { name: "wand", icon: WandSparkles },
  { name: "sparkles", icon: Sparkles },
  { name: "zap", icon: Zap },
  { name: "brain", icon: Brain },
  { name: "bot", icon: Bot },
  // Financial
  { name: "dollar", icon: DollarSign },
  { name: "coins", icon: Coins },
  { name: "banknote", icon: Banknote },
  { name: "piggy-bank", icon: PiggyBank },
  { name: "landmark", icon: Landmark },
  { name: "chart-line", icon: ChartLine },
  { name: "chart-pie", icon: ChartPie },
  { name: "briefcase", icon: Briefcase },
  // Communication
  { name: "phone", icon: Phone },
  { name: "mail", icon: Mail },
  { name: "send", icon: Send },
  // Scheduling
  { name: "calendar", icon: Calendar },
  { name: "clock", icon: Clock },
  { name: "bell", icon: Bell },
  // Documents
  { name: "document", icon: FileText },
  // People
  { name: "household", icon: Users },
  { name: "handshake", icon: Handshake },
  // Recognition
  { name: "star", icon: Star },
  { name: "check", icon: CheckCircle },
]

export function getIconComponent(iconName: string | undefined): LucideIcon {
  if (!iconName) return WandSparkles
  const found = ICON_OPTIONS.find((opt) => opt.name === iconName)
  return found?.icon || WandSparkles
}

interface IconColorPickerInputProps {
  id: string
  label?: string
  iconValue?: string
  colorValue?: string
  onIconChange: (value: string) => void
  onColorChange: (value: string) => void
}

export function IconColorPickerInput({
  id,
  label,
  iconValue,
  colorValue,
  onIconChange,
  onColorChange,
}: IconColorPickerInputProps) {
  const [isIconOpen, setIsIconOpen] = useState(false)

  const selectedColor = colorValue || COLOR_OPTIONS.blue

  const selectedIcon = ICON_OPTIONS.find((opt) => opt.name === iconValue) || ICON_OPTIONS[0]
  const SelectedIconComponent = selectedIcon.icon

  return (
    <div className="space-y-1">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="border-input flex h-9 overflow-hidden rounded-md border">
        {/* Color picker on left */}
        <ColorPicker
          value={selectedColor}
          onChange={onColorChange}
          trigger={
            <button
              type="button"
              className="hover:bg-accent flex flex-shrink-0 items-center justify-center px-2.5 transition-colors"
            >
              <div className="h-4 w-4 rounded-full" style={{ backgroundColor: selectedColor }} />
            </button>
          }
        />

        {/* Divider */}
        <div className="bg-input w-px" />

        {/* Icon picker on right */}
        <Popover open={isIconOpen} onOpenChange={setIsIconOpen}>
          <PopoverTrigger asChild>
            <Button id={id} variant="ghost" className="h-full flex-1 justify-start gap-2 rounded-none px-3">
              <SelectedIconComponent className="h-4 w-4" />
              <span className="text-muted-foreground capitalize">{selectedIcon.name.replace(/-/g, " ")}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-fit p-2" align="start">
            <div className="grid grid-cols-6 gap-1">
              {ICON_OPTIONS.map((option) => {
                const IconComponent = option.icon
                return (
                  <button
                    key={option.name}
                    type="button"
                    onClick={() => {
                      onIconChange(option.name)
                      setIsIconOpen(false)
                    }}
                    className={cn(
                      "hover:bg-accent flex size-8 items-center justify-center rounded-md transition-all",
                      iconValue === option.name && "bg-accent ring-primary ring ring-offset-1"
                    )}
                    aria-label={`Select ${option.name} icon`}
                  >
                    <IconComponent className="size-4.5" />
                  </button>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
